import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { sameValue, useDirtyForm } from '../../../hooks/useDirtyForm';
import { useToast } from '../../shared/Toast';
import {
  SizeGroup, SizeItemBody, createSizeGroup, createSizeItem, deleteSizeGroup, deleteSizeItem, listSizeGroups,
  updateSizeGroup, updateSizeItem,
} from './api';
import {
  BADGE_BRAND, BTN, BTN_GHOST, BTN_PRIMARY, CHANGED, CardSaveBar, EmptyState, FIELD, HINT, ICON_BTN, INVALID, LABEL,
  LibraryPane, LoadState, SearchField, euro, searchKey, similarName, useCatalogLock, useLibraryErrorText,
} from './parts';
import { RowDraft, RowsEditor, newRowKey, rowKeyOf, validateRows } from './RowsEditor';
import { useLibraryDraftGuard } from './draftGuard';

/**
 * Κατάλογος → «Ομάδες μεγεθών» (mock Φάσης 3, 12/10/2026). Έτοιμα σετ μεγεθών με τιμές: η «Εισαγωγή»
 * στο προϊόν τα ΑΝΤΙΓΡΑΦΕΙ (api.sizeGroupToProductSizes), άρα μια αλλαγή εδώ δεν φτάνει σε προϊόντα
 * που τα πήραν ήδη — γι' αυτό εδώ δεν υπάρχει «σε N προϊόντα».
 * Ε10α: η τιμή μέλους είναι η ΤΕΛΙΚΗ τιμή του μεγέθους («3,40 €», όχι «+3,40»). Ε13α: όχι νέα
 * αρνητική τιμή· μια παλιά αρνητική μένει όσο δεν την αλλάζεις (ο server τη δέχεται ίδια).
 * Μία κάρτα σε επεξεργασία τη φορά, με «Αποθήκευση» της κάρτας (hooks/useDirtyForm, guardId).
 */

interface SizeDraft { name: string; items: RowDraft[] }

const EMPTY_DRAFT: SizeDraft = { name: '', items: [] };

const blankRow = (): RowDraft => ({ key: newRowKey(), id: null, name: '', price: null, is_default: false });

const draftOf = (g: SizeGroup): SizeDraft => ({
  name: g.name,
  items: g.items.map(i => ({ key: rowKeyOf(i.id), id: i.id, name: i.name, price: i.default_price_modifier, is_default: i.is_default })),
});

const keptRows = (rows: RowDraft[]) => rows.filter(r => r.id != null || r.name.trim() || r.price != null);

type Open = number | 'new' | null;

const SizeGroupsTab: React.FC = () => {
  const { t } = useTranslation(['products', 'common', 'settings']);
  const { store } = useAuth();
  const storeId = store?.id;
  const { showToast } = useToast();
  const errorText = useLibraryErrorText();
  const { locked, noteError } = useCatalogLock();
  const form = useDirtyForm<SizeDraft>(EMPTY_DRAFT, { guardId: 'catalog:size-groups' });
  useLibraryDraftGuard('size-groups', form.dirty);
  const { values: v, saved } = form;

  const [groups, setGroups] = useState<SizeGroup[]>([]);
  const [load, setLoad] = useState<'loading' | 'ready' | 'error'>('loading');
  const [open, setOpen] = useState<Open>(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const nameId = useId();
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const loadGroups = useCallback(async (): Promise<SizeGroup[] | null> => {
    if (!storeId) return null;
    try {
      const list = await listSizeGroups(storeId);
      if (!alive.current) return null;
      setGroups(list);
      setLoad('ready');
      return list;
    } catch (err) {
      console.error('Size groups load failed:', err);
      if (alive.current) setLoad(prev => (prev === 'ready' ? prev : 'error'));
      return null;
    }
  }, [storeId]);

  useEffect(() => {
    setOpen(null);
    form.reset(EMPTY_DRAFT);
    setLoad('loading');
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- μόνο σε αλλαγή καταστήματος
  }, [storeId]);

  // Κλείδωσε (κεντρικός κατάλογος, π.χ. 403 από τον server): το πρόχειρο δεν θα αποθηκευτεί ποτέ.
  useEffect(() => {
    if (!locked || open === null) return;
    setOpen(null);
    form.reset(EMPTY_DRAFT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, open]);

  const openGroup = groups.find(g => g.id === open) || null;

  const confirmDropDraft = (): boolean => {
    if (!form.dirty) return true;
    if (open === 'new') return window.confirm(t('products:libraries.discardNew'));
    const name = (openGroup?.name || v.name).trim() || t('products:libraries.unnamed');
    return window.confirm(t('products:libraries.discard', { name }));
  };

  const switchTo = (next: Open): boolean => {
    if (!confirmDropDraft()) return false;
    setCardError(null);
    setShowErrors(false);
    setOpen(next);
    if (next === null) form.reset(EMPTY_DRAFT);
    else if (next === 'new') form.reset({ name: '', items: [blankRow()] });
    else {
      const g = groups.find(x => x.id === next);
      if (g) form.reset(draftOf(g));
    }
    return true;
  };

  /* ───────────── έλεγχος ───────────── */

  const rows = keptRows(v.items);
  const savedRows = useMemo(() => new Map(saved.items.map(r => [r.key, r])), [saved.items]);
  const rowErrors = validateRows(rows, savedRows, {
    allowNegative: false,
    messages: {
      nameRequired: t('products:libraries.validation.rowNameRequired'),
      duplicate: name => t('products:libraries.validation.rowNameDuplicate', { name }),
      priceInvalid: t('products:libraries.validation.priceInvalid'),
      priceNegative: t('products:libraries.validation.priceNegative'),
    },
  });
  const nameError = (() => {
    const name = v.name.trim();
    if (!name) return t('products:libraries.validation.nameRequired');
    const renamed = open === 'new' || name !== saved.name.trim();
    if (renamed && groups.some(g => g.id !== open && similarName(g.name, name))) return t('products:libraries.validation.nameTaken', { name });
    return null;
  })();
  const valid = !nameError && Object.keys(rowErrors).length === 0;

  /* ───────────── αποθήκευση ───────────── */

  const settle = async (groupId: number, draft: SizeDraft): Promise<boolean> => {
    const fresh = await loadGroups();
    if (!alive.current) return false;
    const g = fresh?.find(x => x.id === groupId);
    if (!fresh || !g) {
      setOpen(null);
      form.reset(EMPTY_DRAFT);
      if (!fresh) setLoad('error');
      return false;
    }
    const nextSaved = draftOf(g);
    const kept = keptRows(draft.items);
    const claimed = new Set(kept.filter(r => r.id != null).map(r => r.id));
    const items = kept.map(r => {
      const name = r.name.trim();
      if (r.id != null) return { ...r, name };
      const match = g.items.find(i => !claimed.has(i.id) && i.name === name);
      if (!match) return { ...r, name };
      claimed.add(match.id);
      return { ...r, key: rowKeyOf(match.id), id: match.id, name };
    });
    const nextValues: SizeDraft = { name: draft.name.trim(), items };
    setOpen(g.id);
    form.reset(nextSaved);
    if (!sameValue(nextSaved, nextValues)) form.setValues(() => nextValues);
    return true;
  };

  const saveExisting = async (g: SizeGroup) => {
    const draft = form.values;
    const kept = keptRows(draft.items);
    let failure: unknown = null;
    try {
      if (draft.name.trim() !== g.name) await updateSizeGroup(g.id, { name: draft.name.trim() });
      for (const item of g.items) {
        if (!kept.some(r => r.id === item.id)) await deleteSizeItem(item.id);
      }
      const savedOrder = g.items.filter(i => kept.some(r => r.id === i.id)).map(i => i.id).join();
      const draftOrder = kept.filter(r => r.id != null).map(r => r.id).join();
      const renumber = kept.some(r => r.id == null) || savedOrder !== draftOrder;
      for (let index = 0; index < kept.length; index++) {
        const r = kept[index];
        const item = r.id == null ? null : g.items.find(x => x.id === r.id);
        if (!item) continue;
        const p: Partial<SizeItemBody> = {};
        if (r.name.trim() !== item.name) p.name = r.name.trim();
        // Μόνο ό,τι άλλαξε: μια παλιά αρνητική τιμή που δεν αγγίχτηκε δεν ξαναστέλνεται.
        if (r.price !== item.default_price_modifier) p.default_price_modifier = r.price as number;
        if (r.is_default !== item.is_default) p.is_default = r.is_default;
        if (renumber && item.display_order !== index) p.display_order = index;
        if (Object.keys(p).length) await updateSizeItem(item.id, p);
      }
      for (let index = 0; index < kept.length; index++) {
        const r = kept[index];
        if (r.id != null) continue;
        await createSizeItem(g.id, { name: r.name.trim(), default_price_modifier: r.price as number, is_default: r.is_default, display_order: index });
      }
    } catch (err) {
      failure = err;
      noteError(err);
    }
    const ok = await settle(g.id, draft);
    if (!alive.current) return;
    if (failure) setCardError(t('products:libraries.errors.partial', { reason: errorText(failure) }));
    else if (ok) showToast('success', t('products:libraries.savedToast', { name: draft.name.trim() }));
    else setListError(t('products:libraries.errors.notFound'));
  };

  const saveNew = async () => {
    if (!storeId) return;
    const draft = form.values;
    try {
      const created = await createSizeGroup(storeId, {
        name: draft.name.trim(),
        items: keptRows(draft.items).map(r => ({ name: r.name.trim(), price_modifier: r.price as number, is_default: r.is_default })),
      });
      await settle(created.id, draftOf(created));
      if (alive.current) showToast('success', t('products:libraries.createdToast', { name: created.name }));
    } catch (err) {
      noteError(err);
      setCardError(errorText(err));
    }
  };

  const save = async () => {
    if (saving || locked) return;
    if (!valid) { setShowErrors(true); return; }
    setSaving(true);
    setCardError(null);
    try {
      if (open === 'new') await saveNew();
      else if (openGroup) await saveExisting(openGroup);
    } finally {
      if (alive.current) { setSaving(false); setShowErrors(false); }
    }
  };

  const reset = () => {
    setCardError(null);
    setShowErrors(false);
    if (open === 'new') {
      if (form.dirty && !window.confirm(t('products:libraries.discardNew'))) return;
      setOpen(null);
      form.reset(EMPTY_DRAFT);
      return;
    }
    form.reset();
  };

  const remove = async (g: SizeGroup) => {
    if (!window.confirm(t('products:libraries.sizes.deleteConfirm', { name: g.name }))) return;
    setListError(null);
    try {
      await deleteSizeGroup(g.id);
    } catch (err) {
      noteError(err);
      setCardError(t('products:libraries.errors.actionFailed', { reason: errorText(err) }));
      return;
    }
    setOpen(null);
    form.reset(EMPTY_DRAFT);
    await loadGroups();
    if (alive.current) showToast('success', t('products:libraries.deletedToast', { name: g.name }));
  };

  /* ───────────── εμφάνιση ───────────── */

  const visible = useMemo(() => {
    const q = searchKey(search.trim());
    if (!q) return groups;
    return groups.filter(g => g.id === open || searchKey(g.name).includes(q) || g.items.some(i => searchKey(i.name).includes(q)));
  }, [groups, search, open]);

  const rowLabels = {
    nameCol: t('products:libraries.sizes.sizeCol'),
    priceCol: t('products:libraries.sizes.priceCol'),
    defaultCol: t('products:libraries.sizes.defaultCol'),
    add: t('products:libraries.sizes.addSize'),
    namePlaceholder: t('products:libraries.sizes.sizePlaceholder'),
    nameAria: (n: number) => t('products:libraries.sizes.sizeName', { n }),
    priceAria: (name: string) => t('products:libraries.sizes.sizePrice', { name }),
    defaultAria: (name: string) => t('products:libraries.sizes.sizeDefault', { name }),
    removeAria: (name: string) => t('products:libraries.removeRow', { name }),
    moveAria: (name: string) => t('products:libraries.moveRow', { name }),
  };

  const editor = (g: SizeGroup | null) => {
    const isNew = g === null;
    const legacyNegative = !isNew && v.items.some(r => r.price != null && r.price < 0);
    return (
      <div
        data-testid={isNew ? 'size-group-new' : `size-group-${g.id}`}
        className="col-span-full rounded-xl border-[1.5px] border-brand-line bg-surface-2 dark:bg-surface-1 p-3.5 shadow-[0_1px_2px_rgb(29_45_70_/_4%)]"
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex min-w-[14rem] flex-1 flex-col gap-1.5 sm:max-w-sm">
            <label htmlFor={nameId} className={LABEL}>
              {isNew ? t('products:libraries.sizes.newTitle') : t('products:libraries.sizes.nameLabel')}
            </label>
            <input
              id={nameId}
              type="text"
              value={v.name}
              maxLength={100}
              autoFocus
              placeholder={t('products:libraries.sizes.namePlaceholder')}
              onChange={e => form.setField('name', e.target.value)}
              aria-invalid={showErrors && nameError ? true : undefined}
              className={`${FIELD} ${!isNew && v.name !== saved.name ? CHANGED : ''} ${showErrors && nameError ? INVALID : ''}`}
            />
            {showErrors && nameError && <p className="text-xs text-red-700 dark:text-red-300">{nameError}</p>}
          </div>
          <p className={`${HINT} max-w-md pb-2`}>{t('products:libraries.sizes.copyNote')}</p>
        </div>
        <div className="mt-3.5 max-w-2xl">
          <RowsEditor
            rows={v.items}
            saved={isNew ? new Map(v.items.map(r => [r.key, r])) : savedRows}
            onChange={items => form.setField('items', items)}
            exclusiveDefault
            newRowPrice={null}
            labels={rowLabels}
            errors={rowErrors}
            showErrors={showErrors}
            hint={t('products:libraries.sizes.priceHint')}
          />
          {legacyNegative && <p className="mt-2 text-xs text-amber-800 dark:text-amber-300">{t('products:libraries.sizes.legacyNegative')}</p>}
        </div>
        <div className="mt-3">
          <CardSaveBar
            dirty={form.dirty}
            saving={saving}
            canSave={!showErrors || valid}
            onSave={save}
            onReset={reset}
            isNew={isNew}
            onClose={() => switchTo(null)}
            error={cardError}
            leading={!isNew && g && (
              <button type="button" onClick={() => remove(g)} className={`${BTN_GHOST} !text-red-700 dark:!text-red-300`}>
                <Trash2 className="h-[15px] w-[15px]" aria-hidden="true" />
                {t('products:libraries.sizes.deleteGroup')}
              </button>
            )}
          />
        </div>
      </div>
    );
  };

  const card = (g: SizeGroup) => (
    <div key={g.id} data-testid={`size-group-${g.id}`} className="rounded-xl border border-line bg-surface-2 dark:bg-surface-1 px-3 pb-1 pt-2">
      <div className="flex items-center gap-2 pb-1">
        <b className="min-w-0 truncate text-sm font-semibold text-t1">{g.name}</b>
        <span className={HINT}>{t('products:libraries.sizes.count', { count: g.items.length })}</span>
        <span className="flex-1" />
        {!locked && (
          <button
            type="button"
            onClick={() => switchTo(g.id)}
            aria-label={t('products:libraries.edit', { name: g.name })}
            title={t('products:libraries.edit', { name: g.name })}
            className={ICON_BTN}
          >
            <Pencil className="h-[15px] w-[15px]" aria-hidden="true" />
          </button>
        )}
      </div>
      {g.items.length === 0 ? (
        <p className="border-t border-line py-2 text-[13px] text-t4">{t('products:libraries.sizes.noSizes')}</p>
      ) : g.items.map(item => (
        <div key={item.id} className="flex items-center justify-between gap-2 border-t border-line py-[7px] text-[13.5px]">
          <span className="flex min-w-0 items-center gap-1.5 text-t1">
            <span className="truncate">{item.name}</span>
            {item.is_default && <span className={BADGE_BRAND}>{t('products:libraries.sizes.defaultCol')}</span>}
          </span>
          <b className="font-semibold tabular-nums text-t1">{euro(item.default_price_modifier)}</b>
        </div>
      ))}
    </div>
  );

  return (
    <LibraryPane
      testId="size-groups-tab"
      title={t('products:libraries.sizes.title')}
      count={load === 'ready' ? groups.length : null}
      hint={t('products:libraries.sizes.hint')}
      locked={locked}
      actions={(
        <>
          <SearchField value={search} onChange={setSearch} placeholder={t('products:libraries.sizes.search')} />
          {!locked && (
            <button type="button" onClick={() => switchTo('new')} disabled={load !== 'ready' || open === 'new'} className={BTN_PRIMARY}>
              <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
              {t('products:libraries.sizes.new')}
            </button>
          )}
        </>
      )}
    >
      {listError && (
        <p role="alert" className="mx-[18px] mt-3 rounded-[10px] border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-800 dark:text-red-200">
          {listError}
        </p>
      )}
      {load !== 'ready' ? (
        <LoadState state={load} onRetry={() => { setLoad('loading'); loadGroups(); }} />
      ) : groups.length === 0 && open !== 'new' ? (
        <EmptyState
          title={t('products:libraries.sizes.empty')}
          hint={t('products:libraries.sizes.emptyHint')}
          action={!locked && (
            <button type="button" onClick={() => switchTo('new')} className={BTN}>
              <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
              {t('products:libraries.sizes.new')}
            </button>
          )}
        />
      ) : (
        <div className="flex-1 min-h-0 overflow-auto bg-surface-1 dark:bg-surface-0 mt-3 border-t border-line">
          {visible.length === 0 && open !== 'new' ? (
            <EmptyState title={t('products:libraries.noMatches', { query: search.trim() })} />
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] content-start gap-3 p-4">
              {open === 'new' && editor(null)}
              {visible.map(g => (g.id === open && !locked ? <React.Fragment key={g.id}>{editor(g)}</React.Fragment> : card(g)))}
            </div>
          )}
        </div>
      )}
    </LibraryPane>
  );
};

export default SizeGroupsTab;
